import React, { useState } from "react";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, NA_LABEL } from "src/constants/constants";
import { Column, Row, Text, Card, Divider, Expander, Icon, Button, Loader } from "@amzn/meridian";
import { InspectionResult, isOverridden } from "src/services/inspectionService";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import ComplianceResultRow from "./ComplianceResultRow";
import ComplianceDetailSheet from "./ComplianceDetailSheet";
import CountDetailSheet from "./CountDetailSheet";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import "./ReviewCheckpointCard.scss";

export interface ReviewCheckpointCardProps {
    questionNumber: string;
    questionText: string;
    isAI: boolean;
    isAutoCalculated?: boolean;
    allCompliant: boolean;
    isNA?: boolean;
    isAnalysisPending?: boolean;
    results: InspectionResult[];
    presignedUrlMap: Record<string, string>;
    onOverride?: (result: InspectionResult) => void;
}

const ReviewCheckpointCard: React.FC<ReviewCheckpointCardProps> = ({
    questionNumber,
    questionText,
    isAI,
    isAutoCalculated = false,
    results,
    allCompliant,
    isNA = false,
    isAnalysisPending = false,
    presignedUrlMap,
    onOverride,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedResult, setSelectedResult] = useState<InspectionResult | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const openSheet = (result: InspectionResult) => {
        setSelectedResult(result);
        setSheetOpen(true);
    };

    const handleOverride = (result: InspectionResult) => {
        setSheetOpen(false);
        onOverride?.(result);
    };

    return (
        <Card spacingInset="none">
            <Column spacingInset="400" spacing="300">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h100" color="primary">
                        {questionNumber}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        {isAnalysisPending ? (
                            <Row spacing="200" alignmentVertical="center">
                                <Text type="b100" color="secondary">Analyzing...</Text>
                                <Loader size="small" type="circular" />
                            </Row>
                        ) : isNA ? (
                            <ThemedTag text={NA_LABEL} type="gray" />
                        ) : allCompliant ? (
                            <ThemedTag text={COMPLIANT_LABEL} type="success" />
                        ) : (
                            <ThemedTag text={NON_COMPLIANT_LABEL} type="error" />
                        )}

                        {/* Show AI/Manual/Auto source tag only after analysis is complete */}
                        {!isAnalysisPending && <ThemedTag text={isAutoCalculated ? "Auto" : isAI ? "AI" : "Manual"} type="neutral" />}
                    </Row>
                </Row>

                <Text type="b200" color="secondary">
                    {questionText}
                </Text>

                <Divider />

                {!results[0].objectId ?
                    <Column spacing="200">
                        <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="200 none">
                            <Column spacing="200">
                                <Text type="b200" color="secondary">Answer: {results[0].confirmedResult}</Text>
                                {isOverridden(results[0]) && (<Row>
                                    <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                                </Row>)}
                            </Column>
                            <Button onClick={() => openSheet(results[0])} type="icon">
                                <Icon tokens={chevronRightSmallTokens} color="secondary" />
                            </Button>
                        </Row>
                    </Column> :

                    <Expander
                        title="Detailed compliance status"
                        open={showDetails}
                        onChange={(open: boolean) => setShowDetails(open)}
                        type="inline"
                        spacingInset="none"
                    >
                        <Column spacing="none">
                            {results.map((result, idx) => (
                                <ComplianceResultRow
                                    key={result.resultId}
                                    result={result}
                                    index={idx}
                                    onClick={() => openSheet(result)}
                                    isLast={idx === results.length - 1}
                                />
                            ))}
                        </Column>
                    </Expander>}
            </Column>

            {isAutoCalculated ? (
                <CountDetailSheet
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    result={selectedResult}
                    questionText={questionText}
                    onOverride={onOverride ? handleOverride : undefined}
                />
            ) : (
                <ComplianceDetailSheet
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    result={selectedResult}
                    questionText={questionText}
                    isAI={isAI}
                    isAutoCalculated={isAutoCalculated}
                    presignedUrlMap={presignedUrlMap}
                    onOverride={onOverride ? handleOverride : undefined}
                />
            )}
        </Card>
    );
};

export default ReviewCheckpointCard;
